import { CalendarEvent } from 'calendar-link';

import { buildCalendarLink } from '@libs/src/calendar-links/calendar-links.helper';
import {
  Account,
  AccountGroupMembership,
} from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import { OPPORTUNITY_STATUS } from '@main-client/src/app/opportunity/opportunity-status.enum';
import { OPPORTUNITY_TYPES } from '@main-client/src/app/opportunity/opportunity-types.enum';
import {
  Opportunity,
  OpportunitySubmission,
} from '@main-client/src/app/opportunity/opportunity.model';
import { isAdminOfGroup } from '@main-client/src/app/user/user.helpers';

import compact from 'lodash-es/compact';
import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';

export interface GetEventsParamArgument {
  filter: string;
  groupId: string;
  keyword: string;
  skip: number;
  time: Date;
  user: Account;
}

export interface GetFeedbackEventsParams {
  groupIds: string[];
  limit: number;
  skip: number;
}

export interface FeedbackEvent
  extends Pick<
    Opportunity,
    | '_id'
    | 'slug'
    | 'event_end_date'
    | 'event_start_date'
    | 'title'
    | 'private'
    | 'notification_recipients'
    | 'public_url'
  > {
  editor_accounts: Pick<
    Account,
    '_id' | 'first_name' | 'full_name' | 'last_name'
  >[];
  featured_accounts: Pick<
    Account,
    '_id' | 'first_name' | 'full_name' | 'last_name' | 'portrait_original_url'
  >[];
  owner_group: Pick<Group, '_id' | 'slug'>;
  user_submission?: {
    _id: string;
  };
}

export interface FeedbackEventIsUserInvited extends FeedbackEvent {
  is_user_invited: boolean;
}

export interface GetFeedbackEventsResponse {
  items: FeedbackEvent[];
  total_count: number;
}

export interface EventsGetResponse {
  items: Opportunity[];
  total_count: number;
}

export interface SubmissionGetResponse {
  items: OpportunitySubmission[];
  total_count: number;
}

const BASE_EVENT_STATUS_CHANGE_PROMPT = {
  cancel: 'Cancel',
  confirm: 'Confirm',
};
export const APPROVE_DIALOG_PROMPT = {
  ...BASE_EVENT_STATUS_CHANGE_PROMPT,
  content: `Are you sure you want to approve this Office Hour request?`,
  title: 'Approve Request',
};
export const REJECT_DIALOG_PROMPT = {
  ...BASE_EVENT_STATUS_CHANGE_PROMPT,
  content: `Are you sure you want to decline this Office Hour request?`,
  title: 'Decline Request',
};
export const EVENT_FILTER_TYPES = {
  draft: 'draft',
  past: 'past',
  requested: 'requested',
  rsvps: 'rsvps',
  upcoming: 'upcoming',
};
export const DEFAULT_GET_EVENTS_PARAMS = {
  limit: 10,
  order: -1,
  skip: 0,
  sort: 'event_start_date',
};
const OPPORTUNITY_EVENT_TYPES = [
  `${OPPORTUNITY_TYPES.event}`,
  `${OPPORTUNITY_TYPES.exclusive}`,
];
export const STATUS_PARAM_FOR_FILTER = {
  [EVENT_FILTER_TYPES.upcoming]: { status: OPPORTUNITY_STATUS.active },
  [EVENT_FILTER_TYPES.draft]: { status: OPPORTUNITY_STATUS.draft },
  [EVENT_FILTER_TYPES.past]: { status: OPPORTUNITY_STATUS.active },
  [EVENT_FILTER_TYPES.requested]: { status: OPPORTUNITY_STATUS.requested },
};
const TIME_PARAM_FOR_FILTER = {
  [EVENT_FILTER_TYPES.upcoming]: 'eventEndDateMin',
  [EVENT_FILTER_TYPES.past]: 'eventStartDateMax',
  [EVENT_FILTER_TYPES.requested]: 'eventStartDateMin',
};
const OPPORTUNITY_TYPE_PARAM_FOR_FILTER = {
  [EVENT_FILTER_TYPES.upcoming]: { opportunityType: OPPORTUNITY_EVENT_TYPES },
  [EVENT_FILTER_TYPES.past]: { opportunityType: OPPORTUNITY_EVENT_TYPES },
  [EVENT_FILTER_TYPES.draft]: { opportunityType: OPPORTUNITY_EVENT_TYPES },
  [EVENT_FILTER_TYPES.requested]: { opportunityType: OPPORTUNITY_EVENT_TYPES },
};
const ORDER_PARAM_FOR_FILTER = {
  [EVENT_FILTER_TYPES.upcoming]: { order: 1 },
  [EVENT_FILTER_TYPES.past]: { order: -1 },
  [EVENT_FILTER_TYPES.draft]: { order: -1 },
  [EVENT_FILTER_TYPES.requested]: { order: 1 },
  [EVENT_FILTER_TYPES.rsvps]: { order: -1 },
};
const SORT_PARAM_FOR_FILTER = {
  [EVENT_FILTER_TYPES.upcoming]: { sort: 'event_start_date' },
  [EVENT_FILTER_TYPES.past]: { sort: 'event_start_date' },
  [EVENT_FILTER_TYPES.draft]: { sort: 'event_start_date' },
  [EVENT_FILTER_TYPES.requested]: { sort: 'event_start_date' },
  [EVENT_FILTER_TYPES.rsvps]: { sort: 'opportunityId.event_start_date' },
};

export function getDeleteOpportunityDialogPromptConfig(
  opportunity: Opportunity,
) {
  return {
    cancel: 'Cancel',
    confirm: 'Delete',
    content: $localize`:@@confirmPromptDialog.content.deletingEvent:Are you sure you want to delete "${opportunity.title}"? You cannot undo this operation.`,
    title: 'Deleting Event',
  };
}

function generateGetEventsParamsForUserAccessRights(
  user: Account,
  groupId: string,
) {
  if (user.isAdmin) {
    return groupId ? { groupIds: [groupId] } : {};
  }
  const userId = get(user, '_id');
  const userGroupIds = user.groups.map(
    (groupMembership: AccountGroupMembership) =>
      get(groupMembership, 'document._id'),
  );
  if (!groupId) {
    return {
      'orFilter.featuredAccountIds': [userId],
      'orFilter.groupIds': userGroupIds,
    };
  }
  return { groupIds: [groupId] };
}

export function buildGetParamsForOpportunities(
  groupId: Group['_id'],
  filter: string,
  user: Account,
) {
  const now = Date.now();
  const timeFilter = TIME_PARAM_FOR_FILTER[filter]
    ? { [TIME_PARAM_FOR_FILTER[filter]]: new Date(now).toISOString() }
    : {};
  const nonAdminRequestedFilter =
    !user.isAdmin &&
    !isAdminOfGroup(user, { _id: groupId }) &&
    isEqual(EVENT_FILTER_TYPES.requested, filter);
  return {
    ...SORT_PARAM_FOR_FILTER[filter],
    ...ORDER_PARAM_FOR_FILTER[filter],
    ...(OPPORTUNITY_TYPE_PARAM_FOR_FILTER[filter] || {}),
    ...(STATUS_PARAM_FOR_FILTER[filter] || {}),
    ...timeFilter,
    ...(nonAdminRequestedFilter ? { featuredAccountIds: [user._id] } : {}),
    ...(nonAdminRequestedFilter ? { editorAccountIds: [user._id] } : {}),
    ...generateGetEventsParamsForUserAccessRights(user, groupId),
    ...(filter === EVENT_FILTER_TYPES.rsvps && { ownerGroup: groupId }),
  };
}

export function buildCalendarEventFromEvent(
  opportunity: Opportunity,
): CalendarEvent {
  const pageUrl = window.location.href;
  return {
    description: $localize`:@@eventsView.updatedSubmissionsDialog.eventCalendarDescription:From: ${pageUrl}\n\nJoining info:\n${getJoinInfo(
      opportunity,
    )}`,
    end: new Date(opportunity.event_end_date),
    start: new Date(opportunity.event_start_date),
    title: opportunity.title,
    url: pageUrl,
  };
}

export function buildCalendarLinkFromEvent(opportunity: Opportunity) {
  const calendarEvent = buildCalendarEventFromEvent(opportunity);
  return buildCalendarLink(calendarEvent);
}

export function getJoinInfo(opportunity: Opportunity) {
  const isInPerson = 'in-person' === opportunity.meeting_method;
  const locationName = isInPerson
    ? opportunity?.event_location?.name || ''
    : '';
  const locationAddressPath = isInPerson
    ? 'event_location.address'
    : 'online_location';
  const locationAddress = get(opportunity, locationAddressPath, '');
  const location = isInPerson
    ? compact([locationName, locationAddress]).join(': ')
    : locationAddress;
  const additionalInformationText = opportunity.event_location?.address2 || '';
  return compact([additionalInformationText, location]).join(', ');
}

export interface EventNotifications {
  notification_recipients: Opportunity['notification_recipients'];
  notifications_enabled: Opportunity['notifications_enabled'];
}
